import { NavLink, useLocation } from 'react-router';
import { Icon, type IconName } from './Icon';

export interface SectionNavItem {
  to: string;
  label: string;
  icon: IconName;
  description?: string;
}

/**
 * The middle pane on settings-like pages: a titled list of sections, the current one highlighted. On the bare
 * base path (e.g. /settings) the first section is the one shown, so it is highlighted too.
 */
export function SectionNav({ title, items, footer, base }: { title: string; items: SectionNavItem[]; footer?: React.ReactNode; base?: string }) {
  const { pathname } = useLocation();
  const onBase = base !== undefined && pathname.replace(/\/$/, '') === base;
  return (
    <nav className="section-nav" aria-label={title}>
      <div className="section-nav-title">{title}</div>
      <ul className="plain-list">
        {items.map((item, i) => (
          <li key={item.to}>
            <NavLink to={item.to} className={({ isActive }) => `section-nav-link${isActive || (onBase && i === 0) ? ' active' : ''}`}>
              <span className="section-nav-icon">
                <Icon name={item.icon} size={18} />
              </span>
              <span className="section-nav-text">
                <span>{item.label}</span>
                {item.description && <span className="section-nav-desc">{item.description}</span>}
              </span>
              <span className="section-nav-chevron mobile-only">
                <Icon name="chevron" size={16} />
              </span>
            </NavLink>
          </li>
        ))}
      </ul>
      {footer && <div className="section-nav-footer">{footer}</div>}
    </nav>
  );
}

/** Page heading for a section: title, optional description, optional actions on the right. */
export function PageHeader({ title, description, back, actions }: { title: string; description?: string; back?: () => void; actions?: React.ReactNode }) {
  return (
    <header className="page-header">
      {back && (
        <button className="icon-btn mobile-only" type="button" onClick={back}>
          <Icon name="back" label="Back" />
        </button>
      )}
      <div className="page-header-text">
        <h1>{title}</h1>
        {description && <p className="muted">{description}</p>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </header>
  );
}
